import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { clearChunkLoadRecoveryFlag } from '../chunk-load-recovery';
import { MetaPixelService } from './core/analytics/meta-pixel.service';
import { GtagService } from './core/analytics/gtag.service';
import { SeoService } from './core/seo/seo.service';
import { AppPlatformService } from './core/services/app-platform.service';
import { I18nService } from './core/services/i18n.service';
import { NewArrivalsBadgeService } from './core/services/new-arrivals-badge.service';
import { ProductsService } from './core/services/products.service';
import { LCP_DEFER_MS, scheduleAfterFirstPaint, scheduleAfterInteraction } from './core/utils/lcp-defer.util';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private sub?: Subscription;
  private routerSub?: Subscription;
  private newArrivalsSub?: Subscription;
  private cancelLcpDefer: (() => void) | null = null;
  private cancelPopupDefer: (() => void) | null = null;
  readonly title = 'ZeroTech Stores';
  deferHeavyChrome = false;

  constructor(
    private i18n: I18nService,
    private router: Router,
    private metaPixel: MetaPixelService,
    private gtag: GtagService,
    private seo: SeoService,
    private productsService: ProductsService,
    private newArrivalsBadge: NewArrivalsBadgeService,
    private platform: AppPlatformService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.seo.applyHome();
    this.sub = this.i18n.language$.subscribe((lang) => this.i18n.applyDocumentLanguage(lang));
    if (!this.platform.isBrowser) {
      return;
    }

    clearChunkLoadRecoveryFlag();
    this.gtag.init();
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.seo.applyForUrl(event.urlAfterRedirects);
        this.metaPixel.trackPageView();
        this.gtag.trackPageView(event.urlAfterRedirects);
      });

    // Warm New / جديد badge ids after LCP so this extra GET does not fight the hero.
    this.cancelLcpDefer = scheduleAfterFirstPaint(() => {
      this.newArrivalsSub = this.productsService.getRecentlyAddedProducts(undefined, 24).subscribe({
        next: (products) => this.newArrivalsBadge.register((products ?? []).map((p) => p.id)),
        error: () => undefined,
      });
    }, LCP_DEFER_MS);

    // Meta Pixel + heavy chrome: first gesture (or long idle fallback).
    // Keeps fbevents.js off the Lighthouse LCP/TBT path while still firing for real visitors.
    this.cancelPopupDefer = scheduleAfterInteraction(() => {
      this.deferHeavyChrome = true;
      this.metaPixel.allowNetwork();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.cancelLcpDefer?.();
    this.cancelPopupDefer?.();
    this.sub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.newArrivalsSub?.unsubscribe();
  }
}
